// BL-2681: the queued lot creates and edits an earlier build left on a device become each lot's
// desired state, so the replay that held them can go without losing anyone's unsynced work. Pure: the
// store's one-time migration reads the queue, calls this, and writes the result in one batch.
import { LOT_STATE_WRITE_FIELD_KEYS } from '@auctogon/core-admin/schemas/lot-state-write';
import { isRecord, type JsonObject, lotIdOfGroupKey, type ChangeQueueJson, type ChangeQueueLabel } from './change-queue-entry';
import type { ReplayError } from './change-queue-outcome';
import { applySave, type LotDesiredState } from './lot-desired-state';
import { mediaOf, type MediaAdd, type MediaSave } from './lot-state-media';

/** A queue entry as an earlier build stored it: the op and payload are read here, not by the op list. */
export interface LegacyEntry {
  id: string;
  seq: number;
  op: string;
  payload: unknown;
  status: 'pending' | 'syncing' | 'failed' | 'refused';
  error: ReplayError | null;
  dependsOn: string | null;
  groupKey: string | null;
  attempts: number;
  createdAt: string;
  updatedAt: string;
}

/** What the migration produced: the states to write, and the entries they replace. */
export interface LegacyMigration {
  states: LotDesiredState[];
  /** The ids of every entry folded into a state, in queue order: delete them in the same batch. */
  migratedIds: string[];
}

/** Why a create that was mid-request at the upgrade is held (it may already be on the server). */
const MAY_BE_ON_SERVER: ReplayError = {
  message: 'This lot may already be on the server. Check the lot list, then discard it or retry to send it again.',
  code: null,
  httpStatus: null,
  fieldNames: [],
};

/** Why a create that carried already-uploaded files is held (the new queue cannot attach them). */
const HAS_UPLOADED_FILES: ReplayError = {
  message: 'This lot has photos or documents that were already uploaded, which the new queue cannot attach. Discard it and add the lot again.',
  code: null,
  httpStatus: null,
  fieldNames: [],
};

/** The ops this migration folds. Everything else (moves, status changes, documents, notes) stays in the queue. */
const MIGRATED_OPS: ReadonlySet<string> = new Set([
  'lot.create.place',
  'lot.create.inventory',
  'lot.edit',
  'lot.media.add',
  'lot.media.remove',
  'lot.media.order',
]);

/**
 * @param op - a queued entry's op.
 * @returns true for an op an earlier build queued that this migration folds into a lot state.
 */
export function isLegacyStateOp(op: string): boolean {
  return MIGRATED_OPS.has(op);
}

interface Parsed {
  lotId: string;
  create: { auctionId: string | null; fields: Record<string, ChangeQueueJson>; uploadedFiles: boolean } | null;
  edit: { fields: Record<string, ChangeQueueJson>; baseValues: Record<string, ChangeQueueJson>; overwrite: boolean } | null;
  /** A photo or video add, removal or order (BL-2815). */
  media: MediaSave | null;
  /** A media add whose file is already in storage, which the new queue cannot attach. */
  unattachable: boolean;
  label: ChangeQueueLabel;
}

function recordOf(value: unknown): Record<string, ChangeQueueJson> | null {
  return isRecord(value) ? (value as Record<string, ChangeQueueJson>) : null;
}

function labelOf(value: unknown): ChangeQueueLabel | null {
  if (!isRecord(value) || typeof value.title !== 'string' || typeof value.newLot !== 'boolean') return null;
  return { lotNumber: typeof value.lotNumber === 'string' ? value.lotNumber : null, title: value.title, newLot: value.newLot };
}

function without(record: Record<string, ChangeQueueJson>, keys: readonly string[]): Record<string, ChangeQueueJson> {
  return Object.fromEntries(Object.entries(record).filter(([key]) => !keys.includes(key)));
}

/** Only the keys the lot-state write's `fields` accepts: the strict schema refuses any other. */
function vocabularyOnly(record: Record<string, ChangeQueueJson>): Record<string, ChangeQueueJson> {
  return Object.fromEntries(Object.entries(record).filter(([key]) => LOT_STATE_WRITE_FIELD_KEYS.includes(key)));
}

/** A create that carried files already uploaded: they cannot ride the state's fields. */
const hasUploadedFiles = (record: Record<string, ChangeQueueJson>): boolean => 'stagedMedia' in record || 'stagedDocuments' in record;

/** A legacy photo or video add, removal or order, as a media change for its lot. */
function parseMedia(input: {
  entry: LegacyEntry;
  lotId: string;
  body: Record<string, ChangeQueueJson>;
  call: JsonObject;
  label: ChangeQueueLabel;
}): Parsed {
  const { entry, lotId, body, call, label } = input;
  const base = { lotId, create: null, edit: null, label };
  if (entry.op === 'lot.media.remove') {
    const params = recordOf(call.params);
    const id = typeof params?.mediaId === 'string' ? params.mediaId : null;
    return { ...base, media: { adds: [], removes: id ? [id] : [], order: null }, unattachable: false };
  }
  if (entry.op === 'lot.media.order') {
    const ids = Array.isArray(body.ids) ? body.ids.filter((v): v is string => typeof v === 'string') : [];
    return { ...base, media: { adds: [], removes: [], order: ids.length > 0 ? ids : null }, unattachable: false };
  }
  const payload: JsonObject = isRecord(entry.payload) ? entry.payload : {};
  const stagedFileId = typeof payload.stagedFileId === 'string' ? payload.stagedFileId : null;
  const id = typeof body.id === 'string' ? body.id : null;
  const storagePath = typeof body.storagePath === 'string' ? body.storagePath : null;
  // An add with neither a staged file nor a stored path has nothing to send.
  if (id == null || (stagedFileId == null && storagePath == null)) return { ...base, media: null, unattachable: true };
  const add: MediaAdd = {
    id,
    type: body.type === 'video' ? 'video' : 'photo',
    ...(stagedFileId != null ? { stagedFileId } : {}),
    ...(stagedFileId == null && storagePath != null ? { storagePath } : {}),
    ...(typeof body.posterStoragePath === 'string' ? { posterStoragePath: body.posterStoragePath } : {}),
    ...(typeof payload.stagedPosterFileId === 'string' ? { stagedPosterFileId: payload.stagedPosterFileId } : {}),
    ...(typeof body.caption === 'string' ? { caption: body.caption } : {}),
    sortOrder: 0,
    isCover: false,
  };
  return { ...base, media: { adds: [add], removes: [], order: null }, unattachable: false };
}

/** One entry's lot, fields and label, or null when it is not one this migration can read. */
function parse(entry: LegacyEntry): Parsed | null {
  const lotId = lotIdOfGroupKey(entry.groupKey);
  if (lotId == null || !MIGRATED_OPS.has(entry.op) || !isRecord(entry.payload)) return null;
  const call = isRecord(entry.payload.call) ? entry.payload.call : null;
  const label = labelOf(entry.payload.label);
  // A removal has no body: it names its photo in the path.
  const body = recordOf(call?.body) ?? (entry.op === 'lot.media.remove' ? {} : null);
  if (call == null || label == null || body == null) return null;
  if (entry.op === 'lot.create.place') {
    const [item] = Array.isArray(body.items) ? body.items : [];
    const newLot = isRecord(item) ? recordOf(item.newLot) : null;
    if (newLot == null || typeof body.targetId !== 'string') return null;
    return { lotId, create: { auctionId: body.targetId, fields: vocabularyOnly(without(newLot, ['clientOpId'])), uploadedFiles: hasUploadedFiles(newLot) }, edit: null, media: null, unattachable: false, label };
  }
  if (entry.op === 'lot.create.inventory') {
    return { lotId, create: { auctionId: null, fields: vocabularyOnly(without(body, ['tenantId', 'clientOpId'])), uploadedFiles: hasUploadedFiles(body) }, edit: null, media: null, unattachable: false, label };
  }
  if (entry.op.startsWith('lot.media.')) return parseMedia({ entry, lotId, body, call, label });
  return {
    lotId,
    create: null,
    media: null,
    unattachable: false,
    edit: {
      fields: without(body, ['baseValues', 'overwrite']),
      baseValues: recordOf(body.baseValues) ?? {},
      overwrite: body.overwrite === true,
    },
    label,
  };
}

/**
 * Fold the queue's lot creates and edits into one desired state per lot. A failed entry leaves the
 * lot failed with its reason; a syncing or refused one leaves it pending, because the next pass
 * sends it again and the server's answer (and a Conflict's other side) then comes fresh. An
 * Overwrite the operator already chose is kept, for the fields that edit changed.
 *
 * @param input.entries - the queue, any order.
 * @param input.tenantId - the house the device store is scoped to.
 * @param input.existing - the states already stored, which each lot's entries are folded into.
 * @returns the states to write and the entries to delete with them.
 */
export function legacyEntriesToStates(input: {
  entries: readonly LegacyEntry[];
  tenantId: string;
  /** The lot states already stored: a lot's legacy entries fold into its state, never replace it. */
  existing?: readonly LotDesiredState[];
}): LegacyMigration {
  const byLot = new Map<string, { entry: LegacyEntry; parsed: Parsed }[]>();
  for (const entry of [...input.entries].sort((a, b) => a.seq - b.seq)) {
    const parsed = parse(entry);
    if (parsed == null) continue;
    byLot.set(parsed.lotId, [...(byLot.get(parsed.lotId) ?? []), { entry, parsed }]);
  }
  const existing = new Map((input.existing ?? []).map((s) => [s.lotId, s]));
  const states: LotDesiredState[] = [];
  const migrated: LegacyEntry[] = [];
  for (const [lotId, group] of byLot) {
    let state: LotDesiredState | null = existing.get(lotId) ?? null;
    for (const { entry, parsed } of group) {
      const now = Date.parse(entry.createdAt) || 0;
      state = applySave(state, {
        lotId,
        tenantId: input.tenantId,
        kind: parsed.create ? 'create' : 'edit',
        auctionId: parsed.create?.auctionId ?? null,
        fields: parsed.create?.fields ?? parsed.edit?.fields ?? {},
        baseValues: parsed.edit?.baseValues ?? {},
        // Photos keep the order they were picked in: each takes the next place after those already queued.
        ...(parsed.media ? { media: { ...parsed.media, adds: parsed.media.adds.map((a, i) => ({ ...a, sortOrder: (state ? mediaOf(state).adds.length : 0) + i })) } } : {}),
        label: parsed.label,
        now,
      });
      if (parsed.edit?.overwrite) {
        state = { ...state, overwrite: true, overwriteFields: [...new Set([...state.overwriteFields, ...Object.keys(parsed.edit.fields)])] };
      }
      if (entry.status === 'failed') state = { ...state, status: 'failed', error: entry.error };
      // A create caught mid-request may have landed: the earlier build deduped it by its own id, which the
      // state route does not know, so it is held for the operator rather than sent again as a second lot.
      else if (entry.status === 'syncing' && parsed.create) state = { ...state, status: 'failed', error: MAY_BE_ON_SERVER };
      if (parsed.create?.uploadedFiles || parsed.unattachable) state = { ...state, status: 'failed', error: HAS_UPLOADED_FILES };
      migrated.push(entry);
    }
    if (state != null) states.push(state);
  }
  migrated.sort((a, b) => a.seq - b.seq);
  return { states, migratedIds: migrated.map((entry) => entry.id) };
}
